import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApplicationRoutes } from 'src/constants/applicationRoutes';
import {
  profileInitialSectionStateKey,
  type TabValue
} from 'src/features/userProfileRefactor/travelerProfileConstants';
import { useEnableObtBetaFeatures } from './useEnableObtBetaFeaturesHook';

/**
 * Legacy profile route by default; refactor preview + "(Beta)" labels when user has ObtBeta role.
 */
export function useObtProfileNav() {
  const obtBeta = useEnableObtBetaFeatures();
  const navigate = useNavigate();

  /**
   * Programmatic same-window navigation that lands the user on the Card section.
   * Beta passes a one-time `location.state` signal so TravelerProfileView can seed
   * the active tab; legacy is a single-page profile so no state is needed.
   * Wrapped here because react-router's `navigate(to, options)` requires `state`
   * in the options arg - placing it on the `to` object silently drops it.
   */
  const goToCardSection = useCallback(() => {
    if (obtBeta) {
      navigate(ApplicationRoutes.account.userProfileRefactor, {
        state: { [profileInitialSectionStateKey]: 'card' satisfies TabValue }
      });
      return;
    }
    navigate(ApplicationRoutes.account.userProfileManagement);
  }, [navigate, obtBeta]);

  const labels = useMemo(() => {
    const profilePath = obtBeta
      ? ApplicationRoutes.account.userProfileRefactor
      : ApplicationRoutes.account.userProfileManagement;

    const userProfilesLabel = obtBeta ? 'User Profiles (Beta)' : 'User Profiles';
    const myProfileLabel = obtBeta ? 'My Profile (Beta)' : 'My Profile';

    return { profilePath, userProfilesLabel, myProfileLabel };
  }, [obtBeta]);

  return {
    obtBeta,
    profilePath: labels.profilePath,
    userProfilesLabel: labels.userProfilesLabel,
    myProfileLabel: labels.myProfileLabel,
    goToCardSection
  };
}
